import { useState, useEffect } from 'react';
import { Dialog, api, useToast } from './_barrel.js';


        // Log a new incident. From a subject page `subjectId` is preselected;
        // from the register it is absent and the dialog offers a subject picker.
        function NewIncidentDialog({ token, subjectId, onClose, onSuccess }) {
            const [form, setForm] = useState({
                subject_id: subjectId || '',
                incident_type_code: 'threat',
                title: '',
                summary: '',
                severity: 'moderate',
                confidence: 'medium',
                source: '',
            });
            const [subjects, setSubjects] = useState(null);
            const [submitting, setSubmitting] = useState(false);
            const [error, setError] = useState(null);
            const toast = useToast();

            useEffect(() => {
                if (subjectId) return;
                (async () => {
                    try {
                        const s = await api('/api/v1/subjects?limit=200', {}, token);
                        setSubjects(s.items || []);
                    } catch (err) { setError(err.message); }
                })();
            }, [token, subjectId]);

            const submit = async (e) => {
                e.preventDefault();
                if (!form.subject_id) { setError('Choose the subject this incident concerns.'); return; }
                setSubmitting(true);
                setError(null);
                try {
                    const body = { ...form, subject_id: subjectId || form.subject_id, status: 'draft' };
                    if (!body.source) body.source = null;
                    await api('/api/v1/incidents', { method: 'POST', body: JSON.stringify(body) }, token);
                    toast('Incident logged in draft');
                    onSuccess?.();
                    onClose();
                } catch (err) { setError(err.message); }
                finally { setSubmitting(false); }
            };

            return (
                <Dialog onClose={onClose} title="Log Incident">
                    <form onSubmit={submit} className="space-y-4">
                        {!subjectId && (
                            <div>
                                <label>Subject *</label>
                                <select value={form.subject_id} onChange={e => setForm({...form, subject_id: e.target.value})} required>
                                    <option value="">{subjects ? 'Select a subject…' : 'Loading subjects…'}</option>
                                    {(subjects || []).map(s => <option key={s.id} value={s.id}>{s.display_name || s.full_name}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <label>Incident Type</label>
                            <select value={form.incident_type_code} onChange={e => setForm({...form, incident_type_code: e.target.value})}>
                                <option value="threat">Threat</option>
                                <option value="vulnerability">Vulnerability</option>
                                <option value="hostile_reconnaissance">Hostile reconnaissance</option>
                                <option value="doxxing">Doxxing</option>
                                <option value="harassment">Harassment</option>
                                <option value="impersonation">Impersonation</option>
                                <option value="disinformation">Disinformation</option>
                                <option value="deepfake">Deepfake</option>
                                <option value="breach_exposure">Breach exposure</option>
                                <option value="physical_security">Physical security</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label>Title *</label>
                            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required maxLength="255" />
                        </div>
                        <div>
                            <label>Summary *</label>
                            <textarea rows="4" value={form.summary} onChange={e => setForm({...form, summary: e.target.value})} required></textarea>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label>Severity</label>
                                <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})}>
                                    <option value="low">Low</option>
                                    <option value="moderate">Moderate</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </div>
                            <div>
                                <label>Confidence</label>
                                <select value={form.confidence} onChange={e => setForm({...form, confidence: e.target.value})}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label>Source</label>
                            <input value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="e.g. Tip-off from family security lead" />
                            <div className="text-xs text-muted mt-1">A named source grades <span className="font-mono">B</span> (usually reliable); leave it blank and the incident is graded <span className="font-mono">F</span> — cannot be judged — until you set the grade yourself.</div>
                        </div>
                        <div className="text-xs text-muted">All incidents start in <span className="font-mono gold">draft</span> status. Move to triaged and beyond via sign-off.</div>
                        {error && <div className="text-[var(--red)] text-sm">{error}</div>}
                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn-gold" disabled={submitting}>{submitting ? 'Logging…' : 'Log Incident'}</button>
                        </div>
                    </form>
                </Dialog>
            );
        }

export { NewIncidentDialog };
